/**
 * Contact Form 7 submission tracking.
 *
 * The map & guide request and the contact form are the only true conversions
 * on this site -- everything else we measure is intent, not completion. CF7
 * submits over AJAX and never changes the URL, so there is no thank-you
 * pageview to count and GTM has no native CF7 trigger. CF7 does dispatch DOM
 * events on `document`, which is what this listens for.
 *
 * Failures are tracked as well as successes, deliberately. On a site where one
 * form carries the entire conversion count, a form that silently stops
 * delivering -- a mail misconfiguration, or a spam filter eating real people --
 * is the most expensive thing that can go wrong, and the success count alone
 * can't distinguish "nobody submitted" from "nothing got through".
 *
 * PRIVACY: `event.detail.inputs` carries everything the visitor typed -- name,
 * street address, email, phone. None of it is read here and none of it may be
 * sent to an analytics endpoint. Only the form's identity travels by default.
 *
 * If a specific non-identifying field is genuinely wanted in reporting (the
 * "reason" select is the plausible one -- a fixed list of four values), opt it
 * in explicitly per form:
 *
 *     <form data-track-fields="reason"> ... </form>
 *
 * Only named fields are read, the allowlist is per form, and the default is to
 * send nothing. Do not add free-text fields to it.
 */

/** CF7's DOM events, mapped to the outcome we want recorded. */
const CF7_EVENTS = {
    wpcf7mailsent: { event: 'generate_lead', status: 'sent' },
    wpcf7invalid: { event: 'form_error', status: 'validation_failed' },
    wpcf7spam: { event: 'form_error', status: 'spam_blocked' },
    wpcf7mailfailed: { event: 'form_error', status: 'mail_failed' }
};

/** Longest value we'll record for a name or an allowlisted field. */
const MAX_TEXT = 100;

function clean(value) {
    return String(value).replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT);
}

/**
 * The <form> element for a CF7 submission.
 *
 * CF7 puts the unit tag (`wpcf7-f123-p456-o1`) on a wrapper around the form,
 * so the form itself is a descendant rather than the element with the id.
 */
function formElement(detail) {
    if (!detail || !detail.unitTag) {
        return null;
    }

    const wrapper = document.getElementById(detail.unitTag);

    return wrapper ? wrapper.querySelector('form') : null;
}

/**
 * A readable name for the form.
 *
 * Prefers an explicit `data-form-name`, then the heading the templates render
 * directly above the form (`.column-heading` in map-guide.php and contact.php),
 * and otherwise sends nothing rather than guessing. The page path identifies
 * the form well enough on its own in GA4.
 */
function formName(form) {
    if (!form) {
        return '';
    }

    const explicit = form.getAttribute('data-form-name');

    if (explicit) {
        return clean(explicit);
    }

    const column = form.closest('.form-column-inner');
    const heading = column && column.querySelector('.column-heading h2, .column-heading h3');

    return heading ? clean(heading.textContent) : '';
}

/**
 * Values for fields the form has explicitly allowlisted. Returns {} unless
 * `data-track-fields` is present, which it is not by default.
 */
function allowlistedFields(form) {
    if (!form) {
        return {};
    }

    const allowlist = (form.getAttribute('data-track-fields') || '')
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);

    const fields = {};

    allowlist.forEach(name => {
        const input = form.querySelector('[name="' + name + '"]');

        if (input && input.value) {
            fields['form_' + name] = clean(input.value);
        }
    });

    return fields;
}

/**
 * Handle one CF7 lifecycle event.
 */
function handleFormEvent(domEvent) {
    const mapping = CF7_EVENTS[domEvent.type];

    if (!mapping) {
        return;
    }

    const detail = domEvent.detail || {};
    const form = formElement(detail);

    const payload = {
        event: mapping.event,
        form_status: mapping.status,
        form_id: detail.contactFormId ? String(detail.contactFormId) : '',
        form_destination: window.location.pathname
    };

    const name = formName(form);

    if (name) {
        payload.form_name = name;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign(payload, allowlistedFields(form)));
}

/**
 * Bind the CF7 listeners. CF7 dispatches on `document`, and the forms are in
 * the page at load, so one binding covers every form on the page.
 */
function initFormTracking() {
    Object.keys(CF7_EVENTS).forEach(type => {
        document.addEventListener(type, handleFormEvent);
    });
}

export { initFormTracking };
